import { creatorbase, creatorbaseQueries, PaginatedApiResponseData } from '@round/api';
import { toast } from '@round/creatorbase-ui';
import {
    useAddCreatorToPoolMutation,
    usePoolsList,
    useRemoveCreatorFromPoolMutation,
} from '@round/creatorbase-helpers/api-hooks';
import { useQueryClient } from '@tanstack/react-query';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { isNumber } from 'utility/utility';

const keys = creatorbaseQueries.pools.keys;

// A creator in more than this many pools isn't a real case, so the row doesn't paginate
const PAGE_SIZE = 50;

type Config = { enabled?: boolean };

/**
 * The pools an individual platform account belongs to. Membership is per
 * ClientInfluencerAccount, so switching platform in the modal changes this set.
 */
export function useAccountPools(clientInfluencerAccountId: number | undefined, config?: Config) {
    const queryClient = useQueryClient();
    const { selectedClientId } = useClientSelect();

    const params = {
        client_id: selectedClientId,
        client_influencer_account_id: isNumber(clientInfluencerAccountId)
            ? String(clientInfluencerAccountId)
            : undefined,
        page_size: PAGE_SIZE,
    };
    const queryKey = keys.list(params);

    const isEnabled = config?.enabled !== false && isNumber(clientInfluencerAccountId) && isNumber(selectedClientId);
    const { pools, isLoading, isError } = usePoolsList(params, { enabled: isEnabled });

    // The row is driven by this query, so without an optimistic patch a chip only appears once
    // the refetch lands, which reads as a dropped click.
    const patchCachedPools = (getPools: (current: creatorbase.Pool[]) => creatorbase.Pool[]) => {
        const cached = queryClient.getQueryData<PaginatedApiResponseData<creatorbase.Pool>>(queryKey);
        if (!cached) {
            return;
        }

        const results = getPools(cached.results);
        queryClient.setQueryData(queryKey, {
            ...cached,
            results,
            count: cached.count + results.length - cached.results.length,
        });
    };

    const addCachedPool = (pool: creatorbase.Pool) =>
        patchCachedPools((current) => (current.some(({ id }) => id === pool.id) ? current : [...current, pool]));

    const removeCachedPool = (poolId: number) =>
        patchCachedPools((current) => current.filter(({ id }) => id !== poolId));

    // No pool up front: the row adds to whichever one the user picks, so the pool rides along with
    // each call and these share one mutation key.
    const { addAsync } = useAddCreatorToPoolMutation(undefined, queryClient);
    const { removeAsync } = useRemoveCreatorFromPoolMutation(undefined, queryClient);

    return {
        pools,
        isLoading,
        isError,
        addPool: async (pool: creatorbase.Pool) => {
            if (!isNumber(clientInfluencerAccountId)) {
                return;
            }

            // An in-flight read would otherwise land on top of the patch and drop the chip again
            await queryClient.cancelQueries({ queryKey });
            addCachedPool(pool);

            try {
                await addAsync({ poolId: pool.id, client_influencer_account_id: clientInfluencerAccountId });
            } catch {
                // The rollback undoes this patch, but a failure can also mean the membership we
                // cached is stale, and it would sit that way until the list goes stale on its own
                removeCachedPool(pool.id);
                void queryClient.invalidateQueries({ queryKey });
                toast.error('Could not add creator to the pool');
            }
        },
        removePool: async (pool: creatorbase.Pool) => {
            if (!isNumber(clientInfluencerAccountId)) {
                return;
            }

            await queryClient.cancelQueries({ queryKey });
            removeCachedPool(pool.id);

            try {
                await removeAsync({ poolId: pool.id, clientInfluencerAccountId });
            } catch {
                addCachedPool(pool);
                void queryClient.invalidateQueries({ queryKey });
                toast.error('Could not remove creator from the pool');
            }
        },
    };
}
